import axios from 'axios';
import axiosRetry from 'axios-retry';

export class IncentivesService {
  static #batchTimeSpan = 150;
  static #maxBatchSize = global.dsp.settings.oem.api.max_batch_size ?? 3;

  static #instance = null;
  static #batchTimer = null;

  static #itemQueue = [];
  static #items = new Map();

  static get #client() {
    if (IncentivesService.#instance) {
      return IncentivesService.#instance;
    }

    const client = axios.create();
    axiosRetry(client, {
      retryCondition: (error) => {
        if (axiosRetry.isNetworkError(error)) {
          return true;
        }

        // if retry condition is not specified, by default idempotent requests are retried
        const statusCode = error.response?.status ?? 500;

        // Once 503's start coming in, no retries will work.
        return statusCode >= 500 && statusCode !== 503;
      },

      retryDelay: axiosRetry.exponentialDelay,
    });

    return (IncentivesService.#instance = client);
  }

  static async #doRequest(itemIds, batch = false) {
    let request;

    if (batch) {
      request = IncentivesService.#client.get('/api/bulk-incentives', {
        params: { item_ids: itemIds },
      });
    } else {
      request = IncentivesService.#client.get('/api/incentives', {
        params: { id: itemIds },
      });
    }

    const { data } = await request;

    return data;
  }

  static async #makeBatchRequest() {
    const items = IncentivesService.#itemQueue;
    const itemIds = items.map(({ itemId }) => itemId).join(',');

    // Clear and reset the queue.
    IncentivesService.#itemQueue = [];
    IncentivesService.#batchTimer = clearTimeout(IncentivesService.#batchTimer);

    try {
      const result = await IncentivesService.#doRequest(itemIds, true);

      items.forEach((item) => {
        // Handle the case of single ids resulting in a single item returned.
        const incentives = result[item.itemId] ?? { error: `No result for ${item.itemId}` };

        if (Array.isArray(incentives.finance)) {
          incentives.finance = incentives.finance.pop();
        }

        item.resolve(incentives);
      });
    } catch (error) {
      items.forEach((item) => {
        item.reject(error);
      });
    }
  }

  static #queueRequest(itemId) {
    const itemPromise = new Promise((resolve, reject) => {
      IncentivesService.#itemQueue.push({ itemId, resolve, reject });
    });

    // Batch all requests made close to the same time.
    if (!IncentivesService.#batchTimer) {
      IncentivesService.#batchTimer = setTimeout(() => {
        IncentivesService.#makeBatchRequest();
      }, IncentivesService.#batchTimeSpan);
    }

    // But only batch so many requests at once.
    if (IncentivesService.#itemQueue.length >= IncentivesService.#maxBatchSize) {
      IncentivesService.#makeBatchRequest();
    }

    return itemPromise;
  }

  static async fetch(itemId) {
    // Request the incentives if they're not already loaded/loading.
    if (!IncentivesService.#items.has(itemId)) {
      IncentivesService.#items.set(itemId, IncentivesService.#queueRequest(itemId));
    }

    return IncentivesService.#items.get(itemId);
  }
}
